/**
 * UltraAdminDataHealthPage
 * ────────────────────────
 * Read-only operator panel showing every drift alert emitted by the four
 * schema sentinels: cell / money / datetime / identity.
 *
 * Backend endpoint:  GET /api/ultra-admin/data-health
 *
 * IMPORTANT: This page never mutates anything. It calls the audits with
 * `backfill=False`. Operators eyeball the output and fix the ROOT CAUSE
 * (the source code that's writing the wrong shape) — the auto-remediation
 * lives in the startup migration job, not here.
 */
import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Activity, AlertTriangle, CheckCircle2, RefreshCw,
  Grid3x3, DollarSign, Calendar, Mail,
} from 'lucide-react';
import AdminSidebar from '../components/AdminSidebar';
import UltraAdminTopbar from '../components/UltraAdminTopbar';
import PageIntro from '../components/PageIntro';
import { getPageIntro } from '../lib/pageIntros';
import { useLanguage } from '../contexts/LanguageContext';
import { formatDateTime } from '../lib/datetime';
import { useApi } from '../hooks/useApi';

const CATEGORY_META = {
  cell: {
    icon: Grid3x3,
    color: 'amber',
    label_en: 'Cell shapes',
    label_tr: 'Hücre Formatı',
    desc_en: 'grid_cells / hall_stands.cells / change-request alternative cells',
    desc_tr: 'grid_cells, hall_stands.cells, change-request cells',
  },
  money: {
    icon: DollarSign,
    color: 'emerald',
    label_en: 'Money fields',
    label_tr: 'Para Alanları',
    desc_en: 'amount / currency canonical shape (legacy value/ccy detection)',
    desc_tr: 'amount/currency kanonik formu (eski value/ccy tespiti)',
  },
  datetime: {
    icon: Calendar,
    color: 'sky',
    label_en: 'Date / time',
    label_tr: 'Tarih / Zaman',
    desc_en: 'ISO-8601 UTC enforcement (BSON dates, unix timestamps, naive ISO)',
    desc_tr: 'ISO-8601 UTC zorunluluğu (BSON, unix, naive ISO)',
  },
  identity: {
    icon: Mail,
    color: 'fuchsia',
    label_en: 'Identity',
    label_tr: 'Kimlik',
    desc_en: 'Email canonicalization + phone E.164 + ID classification',
    desc_tr: 'Email canonical + telefon E.164 + ID sınıflandırma',
  },
};

const COLOR_CLASSES = {
  amber: {
    bg: 'bg-amber-50',  ring: 'ring-amber-200',  text: 'text-amber-700',
    iconBg: 'bg-amber-100', badge: 'bg-amber-500',
  },
  emerald: {
    bg: 'bg-emerald-50', ring: 'ring-emerald-200', text: 'text-emerald-700',
    iconBg: 'bg-emerald-100', badge: 'bg-emerald-500',
  },
  sky: {
    bg: 'bg-sky-50',     ring: 'ring-sky-200',     text: 'text-sky-700',
    iconBg: 'bg-sky-100', badge: 'bg-sky-500',
  },
  fuchsia: {
    bg: 'bg-fuchsia-50', ring: 'ring-fuchsia-200', text: 'text-fuchsia-700',
    iconBg: 'bg-fuchsia-100', badge: 'bg-fuchsia-500',
  },
};

export default function UltraAdminDataHealthPage() {
  const { language } = useLanguage();
  const tr = language === 'tr';
  const navigate = useNavigate();

  // ── Auth guard (token presence) ──
  useEffect(() => {
    const token = localStorage.getItem('cop31_ultra_token') || localStorage.getItem('cop31_token');
    if (!token) navigate('/giris?tab=ultra');
  }, [navigate]);

  // ── Data ──
  const {
    data: report,
    loading,
    refetch,
  } = useApi('/ultra-admin/data-health', {
    errorMessage: tr ? 'Sağlık raporu yüklenemedi' : 'Failed to load data health report',
  });
  const refreshing = loading && Boolean(report); // re-fetch in progress

  return (
    <div className="flex min-h-screen bg-gray-50">
      <AdminSidebar />
      <div className="flex-1 flex flex-col min-w-0">
        <UltraAdminTopbar />
        <main className="flex-1 p-6 overflow-y-auto">
          <PageIntro {...getPageIntro('data-health', language)} />

          {/* Header */}
          <div className="flex items-start justify-between flex-wrap gap-4 mb-6">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                <Activity className="h-6 w-6 text-emerald-600" />
                {tr ? 'Veri Sağlığı' : 'Data Health'}
              </h1>
              <p className="text-sm text-gray-500 mt-1 max-w-2xl">
                {tr
                  ? 'Veri formatı drift\'lerinin salt-okunur denetim paneli. Her sentinel değişiklik yapmadan tarar; sorunları kaynak kodda düzeltmek için kullanılır.'
                  : 'Read-only audit panel for data format drift. Every sentinel scans without mutating; use this to spot drift and fix the source code.'}
              </p>
              {report?.scanned_at && (
                <p className="text-xs text-gray-400 mt-2" data-testid="data-health-scanned-at">
                  {tr ? 'Tarama:' : 'Scanned:'} {formatDateTime(report.scanned_at)}
                </p>
              )}
            </div>
            <button
              type="button"
              onClick={() => refetch()}
              disabled={refreshing || loading}
              data-testid="data-health-refresh"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-gray-900 text-white text-sm font-semibold hover:bg-gray-800 disabled:opacity-60"
            >
              <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
              {tr ? 'Yeniden Tara' : 'Re-scan'}
            </button>
          </div>

          {loading && !report && (
            <div className="text-center text-gray-400 py-20" data-testid="data-health-loading">
              {tr ? 'Yükleniyor…' : 'Loading…'}
            </div>
          )}

          {report && (
            <>
              {/* Category cards */}
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 mb-6">
                {Object.entries(report.categories || {}).map(([cat, info]) => {
                  const meta  = CATEGORY_META[cat] || {};
                  const Icon  = meta.icon || Activity;
                  const cls   = COLOR_CLASSES[meta.color] || COLOR_CLASSES.amber;
                  const drift = info.drift_total || 0;
                  return (
                    <div
                      key={cat}
                      className={`p-5 rounded-2xl ring-1 ${cls.ring} ${cls.bg} flex flex-col gap-3`}
                      data-testid={`data-health-card-${cat}`}
                    >
                      <div className="flex items-center justify-between">
                        <div className={`w-10 h-10 rounded-xl ${cls.iconBg} flex items-center justify-center`}>
                          <Icon className={`h-5 w-5 ${cls.text}`} />
                        </div>
                        {drift === 0 ? (
                          <span className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-700">
                            <CheckCircle2 className="h-4 w-4" />
                            {tr ? 'Temiz' : 'Healthy'}
                          </span>
                        ) : (
                          <span className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-wide font-bold text-white px-2 py-0.5 rounded-full ${cls.badge}`}>
                            <AlertTriangle className="h-3 w-3" />
                            {drift} {tr ? 'uyarı' : 'alerts'}
                          </span>
                        )}
                      </div>
                      <div>
                        <p className="text-base font-bold text-gray-900">
                          {tr ? meta.label_tr : meta.label_en}
                        </p>
                        <p className="text-xs text-gray-500 mt-0.5">
                          {tr ? meta.desc_tr : meta.desc_en}
                        </p>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Per-category alert tables */}
              <div className="space-y-6">
                {Object.entries(report.categories || {}).map(([cat, info]) => {
                  const meta = CATEGORY_META[cat] || {};
                  const cls  = COLOR_CLASSES[meta.color] || COLOR_CLASSES.amber;
                  return (
                    <section key={cat} data-testid={`data-health-section-${cat}`}>
                      <h2 className="text-lg font-bold text-gray-900 mb-3 flex items-center gap-2">
                        <span className={`inline-block w-2 h-2 rounded-full ${cls.badge}`} />
                        {tr ? meta.label_tr : meta.label_en}
                        <span className="text-xs text-gray-400 font-normal">
                          ({info.drift_total} {tr ? 'uyarı' : 'alerts'})
                        </span>
                      </h2>

                      {info.drift_total === 0 ? (
                        <div className="bg-emerald-50 ring-1 ring-emerald-200 rounded-xl px-4 py-3 text-sm text-emerald-800 flex items-center gap-2">
                          <CheckCircle2 className="h-4 w-4" />
                          {tr ? 'Bu kategoride drift bulunamadı.' : 'No drift detected in this category.'}
                        </div>
                      ) : (
                        <div className="overflow-x-auto bg-white ring-1 ring-gray-200 rounded-xl">
                          <table className="w-full text-sm">
                            <thead className="bg-gray-50 text-gray-600 text-xs uppercase tracking-wide">
                              <tr>
                                <th className="text-left px-4 py-2">{tr ? 'Koleksiyon' : 'Collection'}</th>
                                <th className="text-left px-4 py-2">{tr ? 'Alan' : 'Field'}</th>
                                <th className="text-left px-4 py-2">{tr ? 'Sebep' : 'Reason'}</th>
                                <th className="text-right px-4 py-2">{tr ? 'Adet' : 'Count'}</th>
                                <th className="text-left px-4 py-2">{tr ? 'Örnek IDler' : 'Sample IDs'}</th>
                                <th className="text-left px-4 py-2">{tr ? 'Örnek Değerler' : 'Sample values'}</th>
                              </tr>
                            </thead>
                            <tbody>
                              {info.alerts.map((a, idx) => (
                                <tr key={idx} className="border-t border-gray-100 hover:bg-gray-50">
                                  <td className="px-4 py-3 font-mono text-xs text-gray-700">{a.collection}</td>
                                  <td className="px-4 py-3 font-mono text-xs text-gray-700">{a.field}</td>
                                  <td className={`px-4 py-3 ${cls.text} font-semibold`}>{a.reason}</td>
                                  <td className="px-4 py-3 text-right font-bold">{a.drift_count}</td>
                                  <td className="px-4 py-3">
                                    {(a.sample_ids || []).slice(0, 5).map((sid, i) => (
                                      <span key={i} className="inline-block font-mono text-[11px] bg-gray-100 px-1.5 py-0.5 rounded mr-1 mb-1">
                                        {sid}
                                      </span>
                                    ))}
                                  </td>
                                  <td className="px-4 py-3">
                                    {(a.sample_values || []).map((v, i) => (
                                      <div key={i} className="font-mono text-[11px] text-gray-500 truncate max-w-xs">
                                        {v}
                                      </div>
                                    ))}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </section>
                  );
                })}
              </div>
            </>
          )}
        </main>
      </div>
    </div>
  );
}
